import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, fileUrl } from "@/lib/api";
import { StatusBadge } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Loader2, Printer, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 border-b border-border py-1.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium text-foreground">{value || "—"}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-6 print-avoid-break">
    <h3 className="mb-2 border-b-2 border-border pb-1 text-sm font-bold uppercase tracking-wider text-foreground">{title}</h3>
    {children}
  </div>
);

export default function AssetReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const [asset, setAsset] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { api.getAsset(id).then(setAsset).catch(() => nav("/assets")); }, [id, nav]);

  const downloadPdf = async () => {
    const el = ref.current;
    if (!el) return;
    setPdfBusy(true);
    el.classList.add("pdf-mode");
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: [10, 10, 12, 10],
        filename: `${(asset.asset_code || "asset")}_${(asset.name || "").replace(/[^a-z0-9]+/gi, "_")}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      }).from(el).save();
      toast.success("PDF downloaded");
    } catch { toast.error("PDF export failed"); }
    finally { el.classList.remove("pdf-mode"); setPdfBusy(false); }
  };

  if (!asset) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const qrValue = `${window.location.origin}/assets/${id}`;

  return (
    <div className="relative z-10 min-h-screen report-root">
      <div className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-border bg-background/90 px-4 py-3 backdrop-blur-xl no-print sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => nav(`/assets/${id}`)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.print()} className="gap-2 tap-scale"><Printer className="h-4 w-4" /> <span className="hidden sm:inline">Print</span></Button>
          <Button data-testid="asset-report-pdf" onClick={downloadPdf} disabled={pdfBusy} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
          </Button>
        </div>
      </div>

      <div ref={ref} className="mx-auto max-w-3xl px-5 py-8 sm:px-10">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4 border-b-2 border-border pb-5 print-avoid-break">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary print-accent">Asset Technical Report</span>
            <h1 className="mt-1 font-mono text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">{asset.name}</h1>
            <div className="mt-2 flex items-center gap-2">
              <span className="font-mono text-sm font-bold text-primary print-accent">{asset.asset_code}</span>
              <StatusBadge value={asset.status} />
              <StatusBadge value={asset.pat_status} />
            </div>
            {asset.source && <p className="mt-1 text-xs text-muted-foreground">{asset.source}</p>}
          </div>
          <div className="shrink-0 rounded-lg bg-white p-2"><QRCodeSVG value={qrValue} size={92} /></div>
        </div>

        {/* Photos */}
        {asset.photos?.length > 0 && (
          <Section title="Photographs">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {asset.photos.map((p) => <img key={p.storage_path} src={fileUrl(p.storage_path)} alt="" className="aspect-square w-full rounded-lg border border-border object-cover" />)}
            </div>
          </Section>
        )}

        {/* Details */}
        <Section title="Details">
          <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            <div>
              <Row label="Category" value={asset.category} />
              <Row label="Make" value={asset.make} />
              <Row label="Model" value={asset.model} />
              <Row label="Serial / ID" value={asset.serial} />
            </div>
            <div>
              <Row label="Location" value={asset.location} />
              <Row label="Purchase date" value={asset.purchase_date} />
              <Row label="Warranty expiry" value={asset.warranty_expiry} />
              <Row label="Value" value={asset.value} />
            </div>
          </div>
          {asset.notes && <p className="mt-2 text-sm">{asset.notes}</p>}
        </Section>

        {/* PAT history */}
        <Section title={`Electrical (PAT) test history · ${asset.pat_status || "Untested"}`}>
          {asset.pat_tests?.length ? (
            <table className="w-full border-collapse text-sm">
              <thead><tr className="border-b-2 border-border text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="py-1 pr-2">Date</th><th className="py-1 pr-2">Result</th><th className="py-1 pr-2">Tested by</th><th className="py-1">Next due</th></tr></thead>
              <tbody>
                {asset.pat_tests.map((t, i) => (
                  <tr key={t.id || i} className="border-b border-border">
                    <td className="py-1 pr-2 font-mono">{t.date}</td>
                    <td className="py-1 pr-2"><StatusBadge value={t.result} /></td>
                    <td className="py-1 pr-2">{t.tested_by}</td>
                    <td className="py-1 font-mono">{t.next_due}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-sm text-muted-foreground">No PAT tests recorded.</p>}
        </Section>

        {/* Faults */}
        <Section title={`Fault history (${asset.faults?.length || 0})`}>
          {asset.faults?.length ? asset.faults.map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-3 border-b border-border py-1.5 text-sm">
              <span>{f.title} {f.description ? <span className="text-muted-foreground">— {f.description}</span> : null}</span>
              <span className="flex shrink-0 gap-1"><StatusBadge value={f.priority} /><StatusBadge value={f.status} /></span>
            </div>
          )) : <p className="text-sm text-muted-foreground">No faults recorded.</p>}
        </Section>

        {/* Maintenance */}
        <Section title={`Service & maintenance history (${asset.maintenance?.length || 0})`}>
          {asset.maintenance?.length ? asset.maintenance.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-3 border-b border-border py-1.5 text-sm">
              <span>{m.title} <span className="text-muted-foreground">· {m.type}{m.scheduled_date ? ` · ${m.scheduled_date}` : ""}{m.cost ? ` · ${m.cost}` : ""}</span></span>
              <StatusBadge value={m.derived_status} />
            </div>
          )) : <p className="text-sm text-muted-foreground">No maintenance recorded.</p>}
        </Section>

        <div className="mt-8 border-t border-border pt-3 text-center font-mono text-xs text-muted-foreground">
          Generated {new Date().toLocaleString()} · Plantagenet Hall Tech Management
        </div>
      </div>
    </div>
  );
}
